<script>
  import {
    deviceCheckOpen,
    deviceCheckReport,
    cameraInterruption,
    renderProcessRecovery,
    recordingHealth,
    recordingSafetyOpen,
    recordingSaveState,
    recoverableRecordings,
    pendingGallerySessions,
    directGalleryStatus
  } from '../store.js';

  import {
    confirmRecoverableRecordingSaved,
    deleteRecoverableRecording,
    finalizeRecoverableRecording,
    loadRecoverableRecordings
  } from '../../media/recording-safety-service.js';
  import {
    deletePendingGallerySession,
    listPendingGallerySessions,
    publishPartialGallerySession
  } from '../../media/direct-gallery-recovery.js';
  import { confirmDialog } from '../../ui/dialogs.js';
  import { estimateRecoveredDurationMs, applyWebmDuration } from '../../recording-utils.js';
  import { saveBlobToDevice, shareSavedFile } from '../../platform-save.js';
  const fmtBytes = value => {
    const bytes = Number(value) || 0;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  function rerun() {
    recordingSafetyOpen.set(false);
    deviceCheckOpen.set(true);
  }

  async function saveRecovery(recording) {
    const fileName = recording.fileName || `Recovered_Recording_${recording.recordingId}.webm`;
    recordingSaveState.set({ status: 'finalizing', message: 'Preparing recovered recording…', fileName, bytes: recording.bytes || 0, recordingId: recording.recordingId });
    try {
      const finished = await finalizeRecoverableRecording(recording.recordingId);
      if (!finished?.blob?.size) throw new Error('Recovery file is empty');
      // Estimate + write the WebM duration header (crash-cut files have none),
      // then save via the platform-aware path — a bare <a download> anchor is
      // a silent no-op inside the Capacitor WebView.
      const durationMs = estimateRecoveredDurationMs(finished.manifest || recording);
      const blob = await applyWebmDuration(finished.blob, durationMs);
      const result = await saveBlobToDevice(blob, fileName, {
        onProgress: f => recordingSaveState.set({ status: 'finalizing', message: `Saving recovered video… ${Math.round(f * 100)}%`, fileName, bytes: blob.size, recordingId: recording.recordingId })
      });
      if (!result.ok) throw result.error || new Error('Recovered video could not be saved.');
      if (result.uri) await shareSavedFile(result.uri, fileName);
      recordingSaveState.set({ status: 'download-started', message: 'Video saved — share or move it, then confirm.', fileName, bytes: blob.size, recordingId: recording.recordingId });
    } catch (error) {
      recordingSaveState.set({ status: 'error', message: error?.message || 'Recovery save failed.', fileName, bytes: recording.bytes || 0, recordingId: recording.recordingId });
    }
  }

  async function confirmRecovery(recordingId) {
    await confirmRecoverableRecordingSaved(recordingId);
    recordingSaveState.set({ status: 'confirmed', message: 'Saved successfully', fileName: '', bytes: 0, recordingId: null });
  }

  async function removeRecovery(recording) {
    if (!(await confirmDialog('Delete this interrupted recording permanently?', { title: 'Delete recording', danger: true }))) return;
    await deleteRecoverableRecording(recording.recordingId);
    await loadRecoverableRecordings();
  }

  async function refreshPendingGallery() {
    const sessions = await listPendingGallerySessions().catch(() => []);
    pendingGallerySessions.set(sessions);
    return sessions;
  }

  async function publishGalleryPartial(session) {
    if (!(Number(session.bytes) > 0)) return;
    recordingSaveState.set({
      status: 'finalizing',
      message: 'Publishing partial recording to Gallery…',
      fileName: session.displayName,
      bytes: session.bytes,
      recordingId: session.recordingId
    });
    try {
      const result = await publishPartialGallerySession(session.sessionId);
      await refreshPendingGallery();
      recordingSaveState.set({
        status: 'saved',
        message: 'Partial recording published to Android Gallery.',
        fileName: session.displayName,
        bytes: session.bytes,
        recordingId: null,
        contentUri: result.contentUri || result.uri || null
      });
    } catch (error) {
      recordingSaveState.set({
        status: 'error',
        message: error?.message || 'Partial Gallery recording could not be published.',
        fileName: session.displayName,
        bytes: session.bytes,
        recordingId: session.recordingId
      });
    }
  }

  function keepGalleryForLater() {
    directGalleryStatus.update(status => ({
      ...status,
      state: 'recovery-pending',
      message: 'Pending Gallery recording retained for later.'
    }));
  }

  async function deleteGalleryPending(session) {
    if (!(await confirmDialog(
      `Delete ${session.displayName || 'this pending Gallery recording'} permanently?`,
      { title: 'Delete Gallery recording', danger: true }
    ))) return;
    try {
      await deletePendingGallerySession(session.sessionId, true);
      await refreshPendingGallery();
    } catch (error) {
      recordingSaveState.set({
        status: 'error',
        message: error?.message || 'Pending Gallery recording could not be deleted.',
        fileName: session.displayName,
        bytes: session.bytes,
        recordingId: session.recordingId
      });
    }
  }
</script>

<div class="safety-backdrop" role="presentation">
  <div class="safety-modal recording-safety-center" role="dialog" aria-modal="true" aria-labelledby="safety-center-title">
    <header class="safety-heading">
      <div>
        <div class="safety-kicker">RECORDING SAFETY CENTER</div>
        <h2 id="safety-center-title">{$recordingHealth.status === 'idle' ? 'Recorder diagnostics' : $recordingHealth.status}</h2>
        <p>Live recording health and recoverable video data.</p>
      </div>
      <button class="safety-close" aria-label="Close Recording Safety Center" on:click={() => recordingSafetyOpen.set(false)}>×</button>
    </header>

    <div class="health-hero health-{$recordingHealth.status}">
      <strong>{$recordingHealth.status === 'action-required' ? 'Action Required' : $recordingHealth.status === 'warning' ? 'Warning' : $recordingHealth.status === 'healthy' ? 'Healthy' : 'Standby'}</strong>
      <span>{$recordingHealth.signals?.message || 'Start recording to see live chunk and frame health.'}</span>
    </div>

    <div class="diagnostic-grid">
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Storage remaining</span></div><b>{$recordingHealth.metrics?.estimatedMinutes ?? $deviceCheckReport?.metrics?.estimatedMinutes ?? '—'} min</b></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Storage source</span></div><b>{$recordingHealth.metrics?.storageSource || $deviceCheckReport?.metrics?.storageSource || '—'}</b><p>{$deviceCheckReport?.metrics?.storageApproximate ? 'Approximate browser quota' : 'Device free-space reading'}</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Dropped frames</span></div><b>{$recordingHealth.metrics?.droppedFramePercent ?? '—'}%</b><p>render-frame estimate</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Chunks secured</span></div><b>{$recordingHealth.metrics?.chunks ?? 0}</b><p>{fmtBytes($recordingHealth.metrics?.bytes)}</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Codec / quality</span></div><b>{$deviceCheckReport?.metrics?.mimeType || '—'}</b><p>{$deviceCheckReport?.trackSettings?.video?.width ? `${$deviceCheckReport.trackSettings.video.width}×${$deviceCheckReport.trackSettings.video.height || '—'}` : 'Not checked'}</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Battery</span></div><b>{$deviceCheckReport?.metrics?.batteryLevel == null ? '—' : `${Math.round($deviceCheckReport?.metrics?.batteryLevel * 100)}%`}</b><p>{$deviceCheckReport?.metrics?.charging ? 'Charging' : 'Not charging / unavailable'}</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Estimated device load</span></div><b>{$recordingHealth.signals?.loadRisk || $deviceCheckReport?.metrics?.loadRisk || '—'}</b><p>Based on quality, FPS and sustained performance.</p></article>
      <article class="diagnostic-card"><div class="diagnostic-card-title"><span>Thermal status</span></div><b>{$recordingHealth.signals?.thermalStatus ?? $deviceCheckReport?.metrics?.thermalStatus ?? 'unavailable'}</b><p>Warning only; quality is never changed mid-recording.</p></article>
    </div>

    <div class="camera-continuity" aria-live="polite">
      <strong>Camera continuity: {$cameraInterruption.status}</strong>
      <span>
        {$cameraInterruption.totalInterruptedMs
          ? `${($cameraInterruption.totalInterruptedMs / 1000).toFixed(1)}s interrupted`
          : 'No interruption recorded'}
      </span>
    </div>

    {#if $renderProcessRecovery.pending}
      <div class="camera-continuity" role="alert">
        <strong>Previous recorder restart detected</strong>
        <span>
          {$renderProcessRecovery.reason === 'renderer-crash' ? 'WebView crashed.' : 'Android reclaimed the WebView.'}
          Recovery files were kept. {$renderProcessRecovery.loopGuarded ? 'Automatic restart was stopped to prevent a crash loop.' : ''}
        </span>
      </div>
    {/if}

    <button class="btn btn-ghost btn-block" on:click={rerun}>Run Device Check Again</button>

    <section class="interrupted-recordings pending-gallery-recordings">
      <h3>Pending Gallery Recordings</h3>
      <p class="muted">
        {$directGalleryStatus.capability} / {$directGalleryStatus.state} ·
        Writer: {$directGalleryStatus.writerKind || 'not active'} ·
        {$directGalleryStatus.chunksAcknowledged || 0} chunks ·
        {fmtBytes($directGalleryStatus.bytesAcknowledged)} acknowledged ·
        {fmtBytes($directGalleryStatus.queuedBytes)} queued ·
        Last ack: {$directGalleryStatus.lastAckAt ? new Date($directGalleryStatus.lastAckAt).toLocaleTimeString() : '—'}
      </p>
      {#if $pendingGallerySessions.length === 0}
        <p class="muted">No pending Android Gallery recordings found.</p>
      {:else}
        {#each $pendingGallerySessions as session (session.sessionId)}
          <article class="recovery-recording-card gallery-recovery-card">
            <div>
              <strong>{session.displayName || 'CricCam Pro recording'}</strong>
              <span>
                {fmtBytes(session.bytes)} · {session.state || 'recoverable'} ·
                {new Date(session.createdAt).toLocaleString()}
              </span>
            </div>
            <div class="recovery-actions">
              {#if Number(session.bytes) > 0}
                <button class="btn btn-primary" on:click={() => publishGalleryPartial(session)}>Publish Partial</button>
                <button class="btn btn-ghost" on:click={keepGalleryForLater}>Keep For Later</button>
                <button class="btn btn-ghost" on:click={() => deleteGalleryPending(session)}>Delete</button>
              {:else}
                <button class="btn btn-ghost" on:click={() => deleteGalleryPending(session)}>Cleanup Empty Row</button>
              {/if}
            </div>
          </article>
        {/each}
      {/if}
    </section>

    <section class="interrupted-recordings">
      <h3>Interrupted Recordings</h3>
      {#if $recoverableRecordings.length === 0}
        <p class="muted">No interrupted recordings found.</p>
      {:else}
        {#each $recoverableRecordings as recording, i_each (i_each)}
          <article class="recovery-recording-card">
            <div><strong>{recording.teams || 'Cricket recording'}</strong><span>{fmtBytes(recording.bytes)} · {new Date(recording.createdAt).toLocaleString()}</span></div>
            <div class="recovery-actions">
              <button class="btn btn-primary" on:click={() => saveRecovery(recording)}>Save Recording</button>
              {#if $recordingSaveState.status === 'download-started' && $recordingSaveState.recordingId === recording.recordingId}
                <button class="btn btn-primary" on:click={() => confirmRecovery(recording.recordingId)}>I Can See The File</button>
              {/if}
              <button class="btn btn-ghost" on:click={() => removeRecovery(recording)}>Delete</button>
            </div>
          </article>
        {/each}
      {/if}
    </section>

    {#if $recordingSaveState.status !== 'idle'}
      <div class="save-state save-{$recordingSaveState.status}" aria-live="polite">
        <strong>{$recordingSaveState.message || $recordingSaveState.status}</strong>
        {#if $recordingSaveState.fileName}<span>{$recordingSaveState.fileName} · {fmtBytes($recordingSaveState.bytes)}</span>{/if}
      </div>
    {/if}
  </div>
</div>
